import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  getList,
  getString,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { QuestionType } from "../types/questionType";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { OpenEndedFieldResponseV1, OpenEndedFieldResponseV1DTO } from "./openEndedFieldResponseV1";
import { GridQuestionResponseV1, GridQuestionResponseV1DTO } from "./gridQuestionResponseV1";
import { GroupQuestionResponseV1, GroupQuestionResponseV1DTO, GroupQuestionResponseV1Enums } from "./groupQuestionResponseV1";


export namespace SubmittedResponseEnums {
  export namespace Response {
    
    export namespace Version1Enums {
      export namespace ResponseTypeV1 {
        export interface OpenEndedDTO {
          case: string;
          fieldResponses: OpenEndedFieldResponseV1DTO[];
        }
        
        export class OpenEnded {
          readonly isResponseTypeV1OpenEnded: boolean = true;
        
          readonly fieldResponses: OpenEndedFieldResponseV1[];
        
        
          constructor(
          fieldResponses: OpenEndedFieldResponseV1[]
          ) {
            this.fieldResponses = fieldResponses;
          }
        
          static fromDTO(dto: OpenEndedDTO): OpenEnded {
            const fieldResponses = getList<OpenEndedFieldResponseV1DTO, OpenEndedFieldResponseV1>(
              dto,
              "fieldResponses",
              OpenEndedFieldResponseV1.fromDTO,
              InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedFieldResponsesError
            );
            return new OpenEnded(
              fieldResponses
            );
          }
        
          toDTO(): OpenEndedDTO {
            const dto: OpenEndedDTO = {
              case: "OPEN_ENDED",
                fieldResponses: this.fieldResponses.map((e: OpenEndedFieldResponseV1) => { return e.toDTO() })
            };
            return dto;
          }
          }
        
        OpenEnded.prototype.toString = function toString(): string {
          return `ResponseTypeV1.OPEN_ENDED(fieldResponses='${this.fieldResponses}')`;
        };
        
        export interface SingleSelectDTO {
          case: string;
          answer: string;
        }
        
        export class SingleSelect {
          readonly isResponseTypeV1SingleSelect: boolean = true;
        
          readonly answer: string;
        
        
          constructor(
          answer: string
          ) {
            SingleSelect.validateAnswer(answer);
            this.answer = answer;
          }
        
          static fromDTO(dto: SingleSelectDTO): SingleSelect {
            const answer = getString(dto, "answer", InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError);
            return new SingleSelect(
              answer
            );
          }
        
          toDTO(): SingleSelectDTO {
            const dto: SingleSelectDTO = {
              case: "SINGLE_SELECT",
                answer: this.answer
            };
            return dto;
          }
          private static validateAnswer = (answer: string): void => {
            if(answer.trim().length === 0) {
              throw new InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerNonEmptyError(
                `Attribute answer cannot be an empty string.`
              );
            }
          }
          
        }
        
        SingleSelect.prototype.toString = function toString(): string {
          return `ResponseTypeV1.SINGLE_SELECT(answer='${this.answer}')`;
        };
        
        export interface MultiSelectDTO {
          case: string;
          answer: string;
        }
        
        export class MultiSelect {
          readonly isResponseTypeV1MultiSelect: boolean = true;
        
          readonly answer: string;
        
        
          constructor(
          answer: string
          ) {
            MultiSelect.validateAnswer(answer);
            this.answer = answer;
          }
        
          static fromDTO(dto: MultiSelectDTO): MultiSelect {
            const answer = getString(dto, "answer", InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError);
            return new MultiSelect(
              answer
            );
          }
        
          toDTO(): MultiSelectDTO {
            const dto: MultiSelectDTO = {
              case: "MULTI_SELECT",
                answer: this.answer
            };
            return dto;
          }
          private static validateAnswer = (answer: string): void => {
            if(answer.trim().length === 0) {
              throw new InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerNonEmptyError(
                `Attribute answer cannot be an empty string.`
              );
            }
          }
          
        }
        
        MultiSelect.prototype.toString = function toString(): string {
          return `ResponseTypeV1.MULTI_SELECT(answer='${this.answer}')`;
        };
        
        export interface RankingDTO {
          case: string;
          answer: string;
        }
        
        export class Ranking {
          readonly isResponseTypeV1Ranking: boolean = true;
        
          readonly answer: string;
        
        
          constructor(
          answer: string
          ) {
            Ranking.validateAnswer(answer);
            this.answer = answer;
          }
        
          static fromDTO(dto: RankingDTO): Ranking {
            const answer = getString(dto, "answer", InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError);
            return new Ranking(
              answer
            );
          }
        
          toDTO(): RankingDTO {
            const dto: RankingDTO = {
              case: "RANKING",
                answer: this.answer
            };
            return dto;
          }
          private static validateAnswer = (answer: string): void => {
            if(answer.trim().length === 0) {
              throw new InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerNonEmptyError(
                `Attribute answer cannot be an empty string.`
              );
            }
          }
          
        }
        
        Ranking.prototype.toString = function toString(): string {
          return `ResponseTypeV1.RANKING(answer='${this.answer}')`;
        };
        
        export interface GridDTO {
          case: string;
          answer: GridQuestionResponseV1DTO[];
        }
        
        export class Grid {
          readonly isResponseTypeV1Grid: boolean = true;
        
          readonly answer: GridQuestionResponseV1[];
        
        
          constructor(
          answer: GridQuestionResponseV1[]
          ) {
            this.answer = answer;
          }
        
          static fromDTO(dto: GridDTO): Grid {
            const answer = getList<GridQuestionResponseV1DTO, GridQuestionResponseV1>(
              dto,
              "answer",
              GridQuestionResponseV1.fromDTO,
              InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError
            );
            return new Grid(
              answer
            );
          }
        
          toDTO(): GridDTO {
            const dto: GridDTO = {
              case: "GRID",
                answer: this.answer.map((e: GridQuestionResponseV1) => { return e.toDTO() })
            };
            return dto;
          }
          }
        
        Grid.prototype.toString = function toString(): string {
          return `ResponseTypeV1.GRID(answer='${this.answer}')`;
        };
        
        export interface GroupDTO {
          case: string;
          answer: GroupQuestionResponseV1DTO[];
        }
        
        export class Group {
          readonly isResponseTypeV1Group: boolean = true;
        
          readonly answer: GroupQuestionResponseV1[];
        
        
          constructor(
          answer: GroupQuestionResponseV1[]
          ) {
            this.answer = answer;
          }
        
          static fromDTO(dto: GroupDTO): Group {
            const answer = getList<GroupQuestionResponseV1DTO, GroupQuestionResponseV1>(
              dto,
              "answer",
              GroupQuestionResponseV1.fromDTO,
              InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError
            );
            return new Group(
              answer
            );
          }
        
          toDTO(): GroupDTO {
            const dto: GroupDTO = {
              case: "GROUP",
                answer: this.answer.map((e: GroupQuestionResponseV1) => { return e.toDTO() })
            };
            return dto;
          }
          }
        
        Group.prototype.toString = function toString(): string {
          return `ResponseTypeV1.GROUP(answer='${this.answer}')`;
        };
        
      
        export type ResponseTypeV1 = OpenEnded | SingleSelect | MultiSelect | Ranking | Grid | Group;
      
        export type ResponseTypeV1DTO = OpenEndedDTO | SingleSelectDTO | MultiSelectDTO | RankingDTO | GridDTO | GroupDTO;
      
        export const fromDTO = (dto: ResponseTypeV1DTO): ResponseTypeV1 => {
          switch (dto.case) {
            case "OPEN_ENDED":
                return OpenEnded.fromDTO(dto as OpenEndedDTO);
            case "SINGLE_SELECT":
                return SingleSelect.fromDTO(dto as SingleSelectDTO);
            case "MULTI_SELECT":
                return MultiSelect.fromDTO(dto as MultiSelectDTO);
            case "RANKING":
                return Ranking.fromDTO(dto as RankingDTO);
            case "GRID":
                return Grid.fromDTO(dto as GridDTO);
            case "GROUP":
                return Group.fromDTO(dto as GroupDTO);
            default:
              throw new InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error(
                  `Case ${dto.case} is not valid case of ResponseTypeV1.`
              );
          }
        };
      }
      
    }
    export interface Version1DTO {
      case: string;
      responseTypeV1: Version1Enums.ResponseTypeV1.ResponseTypeV1DTO;
    }
    
    export class Version1 {
      readonly isResponseVersion1: boolean = true;
    
      readonly responseTypeV1: Version1Enums.ResponseTypeV1.ResponseTypeV1;
    
    
      constructor(
      responseTypeV1: Version1Enums.ResponseTypeV1.ResponseTypeV1
      ) {
        this.responseTypeV1 = responseTypeV1;
      }
    
      static fromDTO(dto: Version1DTO): Version1 {
        const responseTypeV1 = Version1Enums.ResponseTypeV1.fromDTO(getObject(dto, "responseTypeV1", InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error));
        return new Version1(
          responseTypeV1
        );
      }
    
      toDTO(): Version1DTO {
        const dto: Version1DTO = {
          case: "VERSION_1",
            responseTypeV1: this.responseTypeV1.toDTO()
        };
        return dto;
      }
      }
    
    Version1.prototype.toString = function toString(): string {
      return `Response.VERSION_1(responseTypeV1='${this.responseTypeV1}')`;
    };
    
  
    export type Response = Version1;
  
    export type ResponseDTO = Version1DTO;
  
    export const fromDTO = (dto: ResponseDTO): Response => {
      switch (dto.case) {
        case "VERSION_1":
            return Version1.fromDTO(dto as Version1DTO);
        default:
          throw new InvalidSubmittedResponseResponseError(
              `Case ${dto.case} is not valid case of Response.`
          );
      }
    };
  }
  
}
export interface SubmittedResponseDTO {
  questionId: string;
  questionCode: CodeDTO;
  questionType: QuestionType.QuestionTypeDTO;
  question?: SurveyLocalizedTextDTO | null;
  response: SubmittedResponseEnums.Response.ResponseDTO;
}

export class SubmittedResponse {
  readonly isSubmittedResponseSubmittedResponse: boolean = true;

  readonly questionId: LeoUUID;
  readonly questionCode: Code;
  readonly questionType: QuestionType.QuestionType;
  readonly question: SurveyLocalizedText | null;
  readonly response: SubmittedResponseEnums.Response.Response;


  constructor(
  questionId: LeoUUID, 
  questionCode: Code, 
  questionType: QuestionType.QuestionType, 
  question: SurveyLocalizedText | null = null, 
  response: SubmittedResponseEnums.Response.Response
  ) {
    this.questionId = questionId;
    this.questionCode = questionCode;
    this.questionType = questionType;
    this.question = question;
    this.response = response;
  }

  static fromDTO(dto: SubmittedResponseDTO): SubmittedResponse {
    const questionId = getLeoUuid(dto, "questionId", InvalidSubmittedResponseQuestionIdError);
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidSubmittedResponseQuestionCodeError));
    const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidSubmittedResponseQuestionTypeError));
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidSubmittedResponseQuestionError));
    }
    const response = SubmittedResponseEnums.Response.fromDTO(getObject(dto, "response", InvalidSubmittedResponseResponseError));
    return new SubmittedResponse(
      questionId,
      questionCode,
      questionType,
      question,
      response
    );
  }

  toDTO(): SubmittedResponseDTO {
    const dto: SubmittedResponseDTO = {
        questionId: this.questionId.toDTO(),
        questionCode: this.questionCode.toDTO(),
        questionType: QuestionType.toDTO(this.questionType),
        response: this.response.toDTO()
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    return dto;
  }
  }

SubmittedResponse.prototype.toString = function toString(): string {
  return `SubmittedResponse(questionId='${this.questionId}',questionCode='${this.questionCode}',questionType='${this.questionType}',question='${this.question}',response='${this.response}')`;
};

export class InvalidSubmittedResponseError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseError";
  }
}
export class InvalidSubmittedResponseQuestionIdError extends InvalidSubmittedResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseQuestionIdError";
  }
}

export class InvalidSubmittedResponseQuestionCodeError extends InvalidSubmittedResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseQuestionCodeError";
  }
}

export class InvalidSubmittedResponseQuestionTypeError extends InvalidSubmittedResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseQuestionTypeError";
  }
}

export class InvalidSubmittedResponseQuestionError extends InvalidSubmittedResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseQuestionError";
  }
}

export class InvalidSubmittedResponseResponseError extends InvalidSubmittedResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseError";
  }
}

export class InvalidSubmittedResponseResponseVersion1Error extends InvalidSubmittedResponseResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1Error";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error extends InvalidSubmittedResponseResponseVersion1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedFieldResponsesError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1OpenEndedFieldResponsesError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerNonEmptyError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1SingleSelectAnswerNonEmptyError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectAnswerError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectAnswerError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectAnswerNonEmptyError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1MultiSelectAnswerNonEmptyError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingAnswerError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingAnswerError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingAnswerNonEmptyError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1RankingAnswerNonEmptyError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1GridError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1GridError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1GridAnswerError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1GridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1GridAnswerError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1GroupError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1GroupError";
  }
}

export class InvalidSubmittedResponseResponseVersion1ResponseTypeV1GroupAnswerError extends InvalidSubmittedResponseResponseVersion1ResponseTypeV1GroupError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSubmittedResponseResponseVersion1ResponseTypeV1GroupAnswerError";
  }
}
